import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Plus, Boxes, PackageCheck, AlertTriangle, PackageX, Package,
  Pencil, Trash2, SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { SearchInput } from "@/components/kit/SearchInput";
import { FilterShell } from "@/components/kit/FilterShell";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { ExportButton } from "@/components/kit/ExportButton";
import { DataTable } from "@/components/tables/DataTable";
import { RowActions } from "@/components/tables/RowActions";
import { EmptyState } from "@/components/feedback/EmptyState";
import { TableSkeleton } from "@/components/feedback/Skeletons";
import { ConfirmDialog } from "@/components/feedback/ConfirmDialog";
import { InventoryDrawer } from "./InventoryDrawer";
import { AdjustDrawer } from "./AdjustDrawer";
import { inventoryService, storesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { useAuth } from "@/hooks/useAuth";
import { canWrite, isOwnerRole } from "@/lib/permissions/permissions";
import { INVENTORY_STATUS_META } from "@/lib/constants";

const DEFAULT_FILTERS = { search: "", store: "all", status: "all" };

export default function InventoryPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const writable = canWrite(user?.role, "inventory");
  const owner = isOwnerRole(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setDraft((d) => ({ ...d, status }));
      setApplied((a) => ({ ...a, status }));
    }
    if (searchParams.get("new") === "1" && writable) {
      setEditing(null);
      setDrawerOpen(true);
    }
    if (status || searchParams.get("new")) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = useMemo(() => ({
    search: applied.search,
    store_id: applied.store === "all" ? "" : applied.store,
    status: applied.status === "all" ? "" : applied.status,
  }), [applied]);

  const listQuery = useQuery({ queryKey: ["inventory", params], queryFn: () => inventoryService.list(params) });
  const summaryQuery = useQuery({ queryKey: ["inventory", "summary"], queryFn: inventoryService.summary });
  const storesQuery = useQuery({ queryKey: ["stores", "lite"], queryFn: () => storesService.list() });

  const stores = useMemo(() => storesQuery.data?.items || [], [storesQuery.data]);
  const storeMap = useMemo(() => Object.fromEntries(stores.map((s) => [s.id, s.store_name])), [stores]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["inventory"] });
    qc.invalidateQueries({ queryKey: ["orders"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
  };

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (r) => { setEditing(r); setDrawerOpen(true); };

  const cardFilter = (status) => {
    const next = applied.status === status ? "all" : status;
    setDraft((d) => ({ ...d, status: next }));
    setApplied((a) => ({ ...a, status: next }));
  };

  const confirmDelete = async () => {
    try { await inventoryService.remove(toDelete.id); toast.success("Inventory item deleted"); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setToDelete(null); }
  };

  const s = summaryQuery.data || {};
  const rows = listQuery.data?.items || [];

  const rowMenu = (r) => {
    const items = [{ key: "view", label: writable ? "Edit Details" : "View Details", icon: Pencil, onSelect: () => openEdit(r) }];
    if (writable) {
      items.unshift({ key: "adjust", label: "Adjust Inventory", icon: SlidersHorizontal, onSelect: () => setAdjusting(r) });
    }
    if (owner) {
      items.push({ key: "delete", label: "Delete", icon: Trash2, destructive: true, separatorBefore: true, onSelect: () => setToDelete(r) });
    }
    return items;
  };

  const columns = [
    { key: "sku", header: "SKU", render: (r) => <span className="font-medium text-foreground">{r.sku}</span> },
    { key: "product_name", header: "Product", render: (r) => r.product_name || <span className="text-muted-foreground">—</span> },
    { key: "store", header: "Store", render: (r) => storeMap[r.store_id] || <span className="text-muted-foreground">—</span> },
    { key: "available", header: "Available", align: "right", render: (r) => <span className="font-semibold tabular-nums text-foreground">{r.available}</span> },
    { key: "used", header: "Used", align: "right", render: (r) => <span className="tabular-nums text-muted-foreground">{r.used}</span> },
    {
      key: "location", header: "Location",
      render: (r) => {
        const loc = [r.bin_location, r.shelf_location].filter(Boolean).join(" · ");
        return loc || <span className="text-muted-foreground">—</span>;
      },
    },
    { key: "min_threshold", header: "Threshold", align: "right", render: (r) => <span className="tabular-nums text-muted-foreground">{r.min_threshold}</span> },
    {
      key: "status", header: "Status",
      render: (r) => { const m = INVENTORY_STATUS_META[r.status]; return <StatusBadge label={m.label} tone={m.tone} />; },
    },
    { key: "updated", header: "Updated", render: (r) => formatRelativeTime(r.updated_at) },
    { key: "actions", header: "", align: "right", render: (r) => <RowActions items={rowMenu(r)} /> },
  ];

  const exportColumns = [
    { label: "SKU", key: "sku" },
    { label: "Product", key: "product_name" },
    { label: "Store", get: (r) => storeMap[r.store_id] || "" },
    { label: "Available", key: "available" },
    { label: "Used", key: "used" },
    { label: "Bin", key: "bin_location" },
    { label: "Shelf", key: "shelf_location" },
    { label: "Threshold", key: "min_threshold" },
    { label: "Status", get: (r) => INVENTORY_STATUS_META[r.status]?.label || r.status },
  ];

  return (
    <div className="space-y-6" data-testid="page-inventory">
      <PageHeader
        sticky title="Inventory" description="Live stock levels per store. Quantities update automatically from orders and inbound."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton filename="inventory" columns={exportColumns} rows={rows} testId="inventory-export" />
            {writable && (
              <Button className="gap-2" onClick={openCreate} data-testid="inventory-primary-action"><Plus className="h-4 w-4" />New Item</Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-available-total" label="Available Units" value={s.available_total ?? "—"} icon={Boxes} tone="blue" />
        <StatCard testId="stat-used-total" label="Used Units" value={s.used_total ?? "—"} icon={PackageCheck} tone="neutral" />
        <StatCard testId="stat-low-stock" label="Low Stock" value={s.low_stock ?? "—"} icon={AlertTriangle} tone="amber" onClick={() => cardFilter("low")} active={applied.status === "low"} />
        <StatCard testId="stat-out-of-stock" label="Out of Stock" value={s.out_of_stock ?? "—"} icon={PackageX} tone="red" onClick={() => cardFilter("out")} active={applied.status === "out"} />
      </div>

      <FilterShell
        onApply={() => { setApplied(draft); toast.success("Filters applied"); }}
        onReset={() => { setDraft(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); toast("Filters reset"); }}
      >
        <SearchInput value={draft.search} onChange={(v) => setDraft((d) => ({ ...d, search: v }))} placeholder="SKU, product, INV ID…" />
        <Select value={draft.store} onValueChange={(v) => setDraft((d) => ({ ...d, store: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-store"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All stores</SelectItem>{stores.map((st) => (<SelectItem key={st.id} value={st.id}>{st.store_name}</SelectItem>))}</SelectContent>
        </Select>
        <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
          <SelectTrigger className="h-10 bg-background" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="in_stock">In stock</SelectItem>
            <SelectItem value="low">Low stock</SelectItem>
            <SelectItem value="out">Out of stock</SelectItem>
          </SelectContent>
        </Select>
      </FilterShell>

      {listQuery.isLoading ? (
        <TableSkeleton rows={6} cols={8} />
      ) : (
        <div className="overflow-x-auto">
          <DataTable
            testId="inventory-table"
            paginated
            columns={columns}
            data={rows}
            emptyState={
              <EmptyState icon={Package} title="No inventory found" testId="inventory-empty-state"
                description="Add a stock item or receive an inbound shipment to build your inventory."
                action={writable ? <Button className="gap-2" onClick={openCreate} data-testid="inventory-empty-action"><Plus className="h-4 w-4" /> New Item</Button> : null} />
            }
          />
        </div>
      )}

      <InventoryDrawer open={drawerOpen} onOpenChange={setDrawerOpen} record={editing} stores={stores} writable={writable} onSaved={refresh} />
      <AdjustDrawer open={!!adjusting} onOpenChange={(o) => !o && setAdjusting(null)} record={adjusting} storeName={adjusting ? storeMap[adjusting.store_id] : ""} onSaved={refresh} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete inventory item?" description={`This permanently removes "${toDelete?.sku}". Items with transaction history cannot be deleted.`}
        confirmLabel="Delete item" destructive onConfirm={confirmDelete} />
    </div>
  );
}
